import {
  useCallback, useEffect, useState,
} from 'react';
import { computeDistanceBetween } from 'spherical-geometry-js';
import type { LatLng } from '~/_shared/types/latLng';
import { KeyboardKeys } from '~/_shared/utils/hooks/useKeyPress';
import {
  type MapKeyPressCallback, MapKeyPressPriority, useMapKeyPressContext,
} from '~/_shared/utils/hooks/useMapKeyPressContext';
import { clamp } from '~/_shared/utils/number/number.helpers';
import { useMap } from '../../mapContext';
import { useProjectionOverlay } from '../../useProjectionOverlay';
import { useMapObjectManager } from '../private/useMapObjectManager';

export const useDrawCircle = ({
  isActive,
  onCircleDrawn,
  minPixels = 4,
  maxPixels = 2000,
}: {
  isActive: boolean;
  minPixels?: number;
  maxPixels?: number;
  onCircleDrawn: (params: { center: LatLng; radiusInPixels: number; radiusInMeters: number }) => void;
}) => {
  const manager = useMapObjectManager();
  const map = useMap();
  const {
    getDistanceBetweenPoints,
    moveLatLngByPixelsInDirectionOfToLatLng,
  } = useProjectionOverlay(map);

  const [circleCenter, setCircleCenter] = useState<LatLng | null>(null);
  const [temporaryCircleRadius, setTemporaryCircleRadius] = useState<{
    pixels: number;
    meters: number;
  } | null>(null);

  const calculateRadius = useCallback((center: LatLng, radiusPosition: LatLng): {
    pixels: number;
    meters: number;
  } | null => {
    const radiusInPixels = getDistanceBetweenPoints(center, radiusPosition);
    let radiusInMeters = computeDistanceBetween(center, radiusPosition);

    if (!radiusInPixels) {
      return null;
    }

    const clampedRadiusInPixels = clamp(radiusInPixels, { min: minPixels, max: maxPixels });
    if (clampedRadiusInPixels !== radiusInPixels) {
      const newRadiusPosition = moveLatLngByPixelsInDirectionOfToLatLng(center, radiusPosition, clampedRadiusInPixels);
      if (!newRadiusPosition) {
        return null;
      }

      radiusInMeters = computeDistanceBetween(center, newRadiusPosition);
    }

    return {
      pixels: radiusInPixels,
      meters: radiusInMeters,
    };
  }, [getDistanceBetweenPoints, maxPixels, minPixels, moveLatLngByPixelsInDirectionOfToLatLng]);

  const onMapClick = useCallback((event: google.maps.MapMouseEvent) => {
    if (!event.latLng) {
      return;
    }

    // start drawing - set center
    if (!circleCenter) {
      setCircleCenter({
        lat: event.latLng.lat(),
        lng: event.latLng.lng(),
      });

      return;
    }

    // end drawing - add drawing to the map
    if (circleCenter) {
      const radius = calculateRadius(circleCenter, {
        lat: event.latLng.lat(),
        lng: event.latLng.lng(),
      });

      if (!radius) {
        return;
      }

      onCircleDrawn({ center: circleCenter, radiusInPixels: radius.pixels, radiusInMeters: radius.meters });

      setCircleCenter(null);
      setTemporaryCircleRadius(null);
    }
  }, [calculateRadius, circleCenter, onCircleDrawn]);

  const onMapMouseMove = useCallback((e: google.maps.MapMouseEvent) => {
    if (!circleCenter || !e.latLng) {
      return;
    }

    const radius = calculateRadius(circleCenter, {
      lat: e.latLng.lat(),
      lng: e.latLng.lng(),
    });

    if (!radius) {
      return;
    }

    setTemporaryCircleRadius(radius);
  }, [circleCenter, calculateRadius]);

  useEffect(() => {
    if (!isActive) {
      return;
    }

    const clickListener = manager.addMapClickListener(onMapClick);
    const mouseMoveListener = manager.addMapMouseMoveListener(onMapMouseMove);

    return () => {
      clickListener.remove();
      mouseMoveListener.remove();
    };
  }, [isActive, manager, onMapClick, onMapMouseMove]);

  const { addMapKeyHandler } = useMapKeyPressContext();

  useEffect(() => { // Reset drawing on ESC
    if (!circleCenter) {
      return;
    }

    const onEsc: MapKeyPressCallback = (e) => {
      setCircleCenter(null);
      setTemporaryCircleRadius(null);

      e.stopPropagation();
    };

    const removeKeyHandler = addMapKeyHandler(KeyboardKeys.Escape, MapKeyPressPriority.ResetDrawing, onEsc);
    return removeKeyHandler;
  }, [addMapKeyHandler, circleCenter]);

  return {
    center: circleCenter,
    radiusInPixels: temporaryCircleRadius?.pixels,
    radiusInMeters: temporaryCircleRadius?.meters,
  };
};
